import React, { useEffect, useState } from "react";

import { Box, Stack, Typography, useMediaQuery } from "@mui/material";

import { useAsyncCurrency } from "../../../Components/hooks/useCurrency";
import { formatDecimalNumber } from "../../../utils/common";
import { RiskTypes, savingsPeriodToLabel } from "../constants";
import { type Insight, type SavingsPeriod } from "../types";
import { calculateSavings, totalCustomProblems, totalPotentialDailySavings, totalRisks } from "../utils";

type InsightImpactPreviewProps = {
  insight: Insight;
  savingsPeriod: SavingsPeriod;
};

const InsightImpactPreview: React.FC<InsightImpactPreviewProps> = ({ insight, savingsPeriod }) => {
  const isMobile = useMediaQuery("(max-width:700px)");

  const { asyncConvertCurrency, customerCurrencySymbol } = useAsyncCurrency();

  const [convertedAndFormatted, setConvertedAndFormatted] = useState<string>();

  const potentialDailySavings = totalPotentialDailySavings(insight);

  useEffect(() => {
    if (!potentialDailySavings) {
      return;
    }
    const potentialDailySavingsForPeriod = calculateSavings(potentialDailySavings);
    asyncConvertCurrency(potentialDailySavingsForPeriod).then((convertedSavings) => {
      setConvertedAndFormatted(`${customerCurrencySymbol}${formatDecimalNumber(convertedSavings, 0)}`);
    });
  }, [asyncConvertCurrency, customerCurrencySymbol, insight, potentialDailySavings]);

  const customProblems = totalCustomProblems(insight);

  const risks = RiskTypes.map((risk) => ({
    label: risk.label,
    value: totalRisks(insight, risk.key),
  }));

  const activeRisk = risks.find((risk) => risk.value);

  const getImpactLabel = (): string => {
    if (potentialDailySavings) {
      return "Potential Savings";
    } else if (activeRisk) {
      return activeRisk.label;
    } else if (customProblems) {
      const customProblemsUnits =
        (customProblems === 1
          ? insight.results?.customProblems?.unitLongSingular
          : insight.results?.customProblems?.unitLongPlural) ?? "";
      return customProblemsUnits;
    }
    return "";
  };

  const getImpactNumber = (): string | null => {
    if (potentialDailySavings) {
      return `${convertedAndFormatted}`;
    } else if (activeRisk) {
      return `${formatDecimalNumber(activeRisk.value, 0)}`;
    } else if (customProblems) {
      return `${formatDecimalNumber(customProblems, 0)}`;
    }
    return null;
  };

  const getUnitsLabel = (): string | null => {
    if (potentialDailySavings) {
      return savingsPeriodToLabel[savingsPeriod];
    }
    return null;
  };

  const impactText = getImpactLabel();
  const impactNumber = getImpactNumber();
  const unitsLabel = getUnitsLabel();

  if (!impactText || !impactNumber) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: isMobile ? "flex-start" : "flex-end", gap: 0.5 }}>
      <Typography data-cy="impact-preview-text" variant="body2">
        {impactText}
      </Typography>

      <Stack direction="row" alignItems="baseline" spacing={0.5}>
        <Typography data-cy="potential-saving" variant="h2" sx={{ fontWeight: 500 }}>
          {impactNumber}
        </Typography>
        {unitsLabel && <Typography variant="subtitle2">/ {unitsLabel}</Typography>}
      </Stack>
    </Box>
  );
};

export default InsightImpactPreview;
